import { inject, Injectable } from '@angular/core';
import { CtaAnalyticsTableService } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.service';
import { EMatchingType } from '@enums/campaign-analytics.enum';
import { ECurrency, EPlatformFeature } from '@enums/campaign.enum';
import {
  IFacebookPostApi,
  IInstagramDataApi,
  IInstagramPostApi,
  IInstagramReelApi,
  IInstagramStoryApi,
  IInstagramStoryDataApi,
  IMediaContentApi,
  ITiktokDataApi,
  ITiktokPostApi,
  ITwitchStreamApi,
  IXPostApi,
  IYoutubeShortApi,
  IYoutubeStreamApi,
  TKpiAnalyticsKey,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyUtilitiesService } from '@utilities/currency-utilities.service';
import { isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ITileConfiguration, ITileKpi } from './social-content-tile.interface';

type TPlatformFeatureSpecificData = Pick<ITileConfiguration, 'title' | 'description' | 'media' | 'duration' | 'kpis'>;

@Injectable({
  providedIn: 'root',
})
export class SocialContentTilePlatformService {
  private readonly kpiIconMap: Record<TKpiAnalyticsKey, string> = {
    avgViewers: 'icons/view.svg',
    comments: 'icons/chat.svg',
    engagementRate: 'icons/rate.svg',
    engagementRateViews: 'icons/rate.svg',
    engagements: 'icons/engagement.svg',
    impressions: 'icons/impression.svg',
    likes: 'icons/thumbs-up.svg',
    mediaValue: 'icons/money.svg',
    peakViewers: 'icons/view.svg',
    predictedImpressions: 'icons/predict.svg',
    quotes: 'icons/quotes.svg',
    reach: 'icons/reach.svg',
    retweets: 'icons/retweet.svg',
    shares: 'icons/share-knowledge.svg',
    sponsoredAvgViewers: 'icons/view.svg',
    sponsoredPeakViewers: 'icons/view.svg',
    sponsoredTotalWatchedHours: 'icons/time.svg',
    totalWatchedHours: 'icons/time.svg',
    views: 'icons/view.svg',
  } as const;

  private readonly ctaAnalyticsTableService = inject(CtaAnalyticsTableService);
  private readonly translate = inject(TranslateService);

  public getPlatformFeatureSpecificData(analyticsNode: IAnalyticsNode): TPlatformFeatureSpecificData {
    const { platformFeature, executionStatus } = analyticsNode;

    switch (platformFeature) {
      case EPlatformFeature.twitchStream:
        return this.getTwitchStreamData(analyticsNode, executionStatus);
      case EPlatformFeature.instagramPost:
      case EPlatformFeature.instagramReel:
      case EPlatformFeature.instagramStory:
      case EPlatformFeature.tiktokPost:
        return this.getInstagramOrTiktokData(analyticsNode);
      case EPlatformFeature.youtubeShort:
      case EPlatformFeature.youtubeVod:
        return this.getYoutubeData(analyticsNode);
      case EPlatformFeature.facebookPost:
        return this.getFacebookData(analyticsNode);
      case EPlatformFeature.xPost:
        return this.getXData(analyticsNode);
      case EPlatformFeature.youtubeStream:
        return this.getYoutubeStreamData(analyticsNode, executionStatus);
      default:
        throw new Error(
          `No getPlatformFeatureSpecificData case implemented for the platform feature: ${platformFeature}`,
        );
    }
  }

  private getSocialPostMedia(media: IMediaContentApi[]): string[] {
    return media.reduce((acc, currentMedia) => {
      if (!currentMedia.thumbnail && !currentMedia.url) {
        return acc;
      }

      acc.push(currentMedia.url ?? currentMedia.thumbnail);

      return acc;
    }, [] as string[]);
  }

  private getTwitchStreamData(
    analyticsNode: IAnalyticsNode,
    executionStatus: EMatchingType,
  ): TPlatformFeatureSpecificData {
    const twitchContent = analyticsNode.content?.socialContent as ITwitchStreamApi;

    return {
      duration:
        analyticsNode.executionTime &&
        executionStatus !== EMatchingType.planned &&
        executionStatus !== EMatchingType.missing
          ? this.ctaAnalyticsTableService.getDuration(analyticsNode.executionTime, twitchContent?.end)
          : null,
      kpis: this.getKpis(analyticsNode),
      media: twitchContent?.twitchMetaData?.thumbnailUrl
        ? [this.ctaAnalyticsTableService.getThumbnailUrl(twitchContent?.twitchMetaData?.thumbnailUrl, 320, 180)]
        : [],
      title: twitchContent?.lastTitle ?? this.mergeTitleFindings(analyticsNode),
    };
  }

  private getInstagramOrTiktokData(analyticsNode: IAnalyticsNode): TPlatformFeatureSpecificData {
    const socialContent: IInstagramPostApi | IInstagramReelApi | IInstagramStoryApi | ITiktokPostApi =
      (analyticsNode.content?.socialContent as
        | IInstagramPostApi
        | IInstagramReelApi
        | IInstagramStoryApi
        | ITiktokPostApi) ?? {};
    let socialContentData: IInstagramDataApi | ITiktokDataApi | IInstagramStoryDataApi | null = null;

    if ('instagramPostData' in socialContent) {
      socialContentData = socialContent.instagramPostData;
    }

    if ('instagramReelData' in socialContent) {
      socialContentData = socialContent.instagramReelData;
    }

    if ('instagramStoryData' in socialContent) {
      socialContentData = socialContent.instagramStoryData;
    }

    if ('tiktokData' in socialContent) {
      socialContentData = socialContent.tiktokData;
    }

    return {
      description: socialContentData?.caption ?? this.mergeCaptionFindings(analyticsNode),
      kpis: this.getKpis(analyticsNode),
      media: this.getSocialPostMedia(socialContent?.media ?? []),
    };
  }

  private getYoutubeData(analyticsNode: IAnalyticsNode): TPlatformFeatureSpecificData {
    const youtubeShortContent = analyticsNode.content?.socialContent as IYoutubeShortApi;

    return {
      description: youtubeShortContent?.youtubeData?.description ?? this.mergeDescriptionFindings(analyticsNode),
      kpis: this.getKpis(analyticsNode),
      media: youtubeShortContent?.youtubeData?.originalUrl ? [youtubeShortContent?.youtubeData?.originalUrl] : [],
      title: youtubeShortContent?.youtubeData?.title ?? this.mergeTitleFindings(analyticsNode),
    };
  }

  private getFacebookData(analyticsNode: IAnalyticsNode): TPlatformFeatureSpecificData {
    const facebookContent = analyticsNode.content?.socialContent as IFacebookPostApi;

    return {
      description: facebookContent?.facebookData?.message ?? this.mergeMessageFindings(analyticsNode),
      kpis: this.getKpis(analyticsNode),
      media: this.getSocialPostMedia(facebookContent?.media ?? []),
    };
  }

  private getXData(analyticsNode: IAnalyticsNode): TPlatformFeatureSpecificData {
    const xContent = analyticsNode.content?.socialContent as IXPostApi;

    return {
      description: xContent?.xData?.message ?? this.mergeMessageFindings(analyticsNode),
      kpis: this.getKpis(analyticsNode),
      media: this.getSocialPostMedia(xContent?.media ?? []),
    };
  }

  private getYoutubeStreamData(
    analyticsNode: IAnalyticsNode,
    executionStatus: EMatchingType,
  ): TPlatformFeatureSpecificData {
    const youtubeContent = analyticsNode.content?.socialContent as IYoutubeStreamApi;

    return {
      description: youtubeContent?.youtubeLiveData?.description ?? this.mergeDescriptionFindings(analyticsNode),
      duration:
        analyticsNode.executionTime &&
        executionStatus !== EMatchingType.planned &&
        executionStatus !== EMatchingType.missing
          ? this.ctaAnalyticsTableService.getDuration(analyticsNode.executionTime, youtubeContent?.end)
          : null,
      kpis: this.getKpis(analyticsNode),
      media: youtubeContent?.youtubeLiveData?.thumbnailUrl
        ? [this.ctaAnalyticsTableService.getThumbnailUrl(youtubeContent?.youtubeLiveData?.thumbnailUrl, 320, 180)]
        : [],
      title: youtubeContent?.lastTitle ?? this.mergeTitleFindings(analyticsNode),
    };
  }

  private mergeMessageFindings(analyticsNode: IAnalyticsNode): string {
    const messages =
      analyticsNode.emptyCtaAnalytics?.messages
        ?.map((message) => message.keywords?.map((keyword) => keyword.keyword).join(' ') ?? '')
        .join(' ') ?? '';

    return messages;
  }

  private mergeCaptionFindings(analyticsNode: IAnalyticsNode): string {
    const captions =
      analyticsNode.emptyCtaAnalytics?.captions
        ?.map((caption) => caption.keywords?.map((keyword) => keyword.keyword).join(' ') ?? '')
        .join(' ') ?? '';

    return captions;
  }

  private mergeTitleFindings(analyticsNode: IAnalyticsNode): string {
    return (
      analyticsNode.emptyCtaAnalytics?.titles
        ?.map((title) => title.keywords?.map((keyword) => keyword.keyword).join(' ') ?? '')
        .join(' ') ?? ''
    );
  }

  private mergeDescriptionFindings(analyticsNode: IAnalyticsNode): string {
    return (
      analyticsNode.emptyCtaAnalytics?.descriptions
        ?.map((desc) => desc.keywords?.map((keyword) => keyword.keyword).join(' ') ?? '')
        .join(' ') ?? ''
    );
  }

  private getKpis(analyticsNode: IAnalyticsNode): ITileKpi[] {
    switch (analyticsNode.platformFeature) {
      case EPlatformFeature.twitchStream:
      case EPlatformFeature.youtubeStream:
        return this.getStreamKpis(analyticsNode);
      case EPlatformFeature.instagramReel:
      case EPlatformFeature.instagramPost:
      case EPlatformFeature.instagramStory:
        return this.getInstagramKpis(analyticsNode);
      case EPlatformFeature.youtubeShort:
      case EPlatformFeature.youtubeVod:
      case EPlatformFeature.tiktokPost:
      case EPlatformFeature.facebookPost:
        return this.getTiktokKpis(analyticsNode);
      case EPlatformFeature.xPost:
        return this.getXKpis(analyticsNode);
      default:
        throw new Error(`No getKpis case implemented for the platform feature: ${analyticsNode.platformFeature}`);
    }
  }

  private addMediaValueKpi(kpis: ITileKpi[], analyticsNode: IAnalyticsNode): void {
    const ctas = Array.from(analyticsNode.content?.ctas?.values() ?? []);
    let mediaValue = 0;
    let currency = ECurrency.eur;

    ctas.forEach((cta) => {
      mediaValue += cta?.ctaAnalytics?.mediaValue?.mediaValue ?? 0;
      currency = cta?.ctaAnalytics?.mediaValue?.currency as ECurrency;
    });

    kpis.push({
      icon: CurrencyUtilitiesService.getCurrencyIcon(currency),
      id: uuidv4(),
      kpiKey: 'mediaValue',
      kpiName: this.translate.instant('pages.campaignDetails.ctaAnalytics.kpi.mediaValue'),
      value: mediaValue,
    });
  }

  private getStreamKpis(analyticsNode: IAnalyticsNode): ITileKpi[] {
    const includedKpis = ['avgViewers', 'totalWatchedHours'] as const;

    const kpis = includedKpis.reduce((acc, includedKpi) => {
      const kpi = analyticsNode.content?.socialContent?.kpiAnalytics?.[includedKpi];

      if (isNil(kpi)) {
        return acc;
      }

      acc.push({
        icon: this.kpiIconMap[includedKpi as TKpiAnalyticsKey],
        id: uuidv4(),
        kpiKey: includedKpi,
        kpiName: this.translate.instant(`pages.campaignDetails.ctaAnalytics.kpi.${includedKpi}`),
        value: kpi ?? 0,
      });

      return acc;
    }, [] as ITileKpi[]);

    // Hidden for now
    // this.addMediaValueKpi(kpis, analyticsNode);

    return kpis;
  }

  private getInstagramKpis(analyticsNode: IAnalyticsNode): ITileKpi[] {
    const includedKpis = ['predictedImpressions', 'impressions', 'engagements'] as const;

    const kpis = includedKpis.reduce((acc, includedKpi) => {
      const kpi = analyticsNode.content?.socialContent?.kpiAnalytics?.[includedKpi];

      if (isNil(kpi)) {
        return acc;
      }

      acc.push({
        icon: this.kpiIconMap[includedKpi as TKpiAnalyticsKey],
        id: uuidv4(),
        kpiKey: includedKpi,
        kpiName: this.translate.instant(`pages.campaignDetails.ctaAnalytics.kpi.${includedKpi}`),
        value: kpi ?? 0,
      });

      return acc;
    }, [] as ITileKpi[]);

    // Hidden for now
    // this.addMediaValueKpi(kpis, analyticsNode);

    return kpis;
  }

  private getTiktokKpis(analyticsNode: IAnalyticsNode): ITileKpi[] {
    const includedKpis = ['predictedImpressions', 'impressions', 'views', 'engagements'] as const;

    const kpis = includedKpis.reduce((acc, includedKpi) => {
      const kpi = analyticsNode.content?.socialContent?.kpiAnalytics?.[includedKpi];

      if (isNil(kpi)) {
        return acc;
      }

      acc.push({
        icon: this.kpiIconMap[includedKpi as TKpiAnalyticsKey],
        id: uuidv4(),
        kpiKey: includedKpi,
        kpiName: this.translate.instant(`pages.campaignDetails.ctaAnalytics.kpi.${includedKpi}`),
        value: kpi ?? 0,
      });

      return acc;
    }, [] as ITileKpi[]);

    // Hidden for now
    // this.addMediaValueKpi(kpis, analyticsNode);

    return kpis;
  }

  private getXKpis(analyticsNode: IAnalyticsNode): ITileKpi[] {
    const includedKpis = ['predictedImpressions', 'impressions', 'engagements'] as const;

    const kpis = includedKpis.reduce((acc, includedKpi) => {
      const kpi = analyticsNode.content?.socialContent?.kpiAnalytics?.[includedKpi];

      if (isNil(kpi)) {
        return acc;
      }

      acc.push({
        icon: this.kpiIconMap[includedKpi as TKpiAnalyticsKey],
        id: uuidv4(),
        kpiKey: includedKpi,
        kpiName: this.translate.instant(`pages.campaignDetails.ctaAnalytics.kpi.${includedKpi}`),
        value: kpi ?? 0,
      });

      return acc;
    }, [] as ITileKpi[]);

    // Hidden for now
    // this.addMediaValueKpi(kpis, analyticsNode);

    return kpis;
  }
}
